/**
 * One shared requestAnimationFrame loop for every scroll-driven effect on the page.
 * Callbacks run in ascending `order`, so writers (sections) run before readers (the ball overlay).
 * Only call addTicker from effects: it touches requestAnimationFrame.
 */

export type TickCallback = (time: number, delta: number) => void

interface Subscriber {
    callback: TickCallback
    order: number
    failures: number
}

const subscribers: Subscriber[] = []

let frame = 0
let lastTime = 0

const loop = (now: number): void => {
    const delta = lastTime ? Math.min((now - lastTime) / 1000, 0.1) : 1 / 60
    lastTime = now

    for (const subscriber of [...subscribers]) {
        try {
            subscriber.callback(now / 1000, delta)
        }
        catch (error) {
            subscriber.failures += 1
            console.error(error)

            if (subscriber.failures >= 3) {
                const index = subscribers.indexOf(subscriber)
                if (index >= 0) subscribers.splice(index, 1)
            }
        }
    }

    frame = subscribers.length ? requestAnimationFrame(loop) : 0
}

export const addTicker = (callback: TickCallback, order = 0): (() => void) => {
    const subscriber: Subscriber = { callback, order, failures: 0 }

    subscribers.push(subscriber)
    subscribers.sort((a, b) => a.order - b.order)

    if (!frame) {
        lastTime = 0
        frame = requestAnimationFrame(loop)
    }

    return () => {
        const index = subscribers.indexOf(subscriber)
        if (index >= 0) subscribers.splice(index, 1)

        if (!subscribers.length && frame) {
            cancelAnimationFrame(frame)
            frame = 0
        }
    }
}
